import { useState, useEffect, useCallback, useRef } from 'react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseAutoSaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  debounceMs?: number; // Default 5000 (5 seconds)
  enabled?: boolean;
  savedDisplayMs?: number; // How long to show "Saved" status (default 3000)
  minSavingDisplayMs?: number; // Minimum time to show "Saving" spinner (default 2000)
  skipSavedStatus?: boolean; // If true, go directly from 'saving' to 'idle' (skip 'saved')
}

export interface UseAutoSaveReturn {
  isSaving: boolean;
  saveStatus: SaveStatus;
  lastSavedAt: Date | null;
  error: Error | null;
  hasUnsavedChanges: boolean;
  forceSave: () => Promise<void>;
  clearError: () => void;
  resetSavedData: (data: any) => void;
}

/**
 * Auto-save hook with debouncing and status tracking.
 *
 * Features:
 * - 15-second debounce after last change (configurable)
 * - Save status tracking: idle, saving, saved, error
 * - "Saved" status visible for 3 seconds after success
 * - beforeunload detection for unsaved changes
 * - forceSave() for immediate saves (tab switches)
 * - Error state with retry on next change
 */
export function useAutoSave<T>({
  data,
  onSave,
  debounceMs = 5000,
  enabled = true,
  savedDisplayMs = 3000,
  minSavingDisplayMs = 2000,
  skipSavedStatus = false
}: UseAutoSaveOptions<T>): UseAutoSaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Track the last saved data to detect actual changes
  const lastSavedDataRef = useRef<string>(JSON.stringify(data));
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const savedDisplayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const pendingDataRef = useRef<T | null>(null);

  // Keep refs to latest values for cleanup/unmount saves
  const dataRef = useRef<T>(data);
  const onSaveRef = useRef(onSave);
  const enabledRef = useRef(enabled);

  // Update refs when values change
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Compute hasUnsavedChanges by comparing current data to last saved data
  const currentDataStr = JSON.stringify(data);
  const hasUnsavedChanges = currentDataStr !== lastSavedDataRef.current;

  // Perform the actual save operation
  const performSave = useCallback(async (dataToSave: T) => {
    if (isSavingRef.current) {
      // If already saving, queue this data for after
      pendingDataRef.current = dataToSave;
      return;
    }

    const dataStr = JSON.stringify(dataToSave);

    // Skip if nothing has changed
    if (dataStr === lastSavedDataRef.current) {
      return;
    }

    isSavingRef.current = true;
    setSaveStatus('saving');
    setError(null);

    const saveStartTime = Date.now();

    try {
      await onSave(dataToSave);

      // Calculate how long the save took and wait for minimum display time
      const elapsed = Date.now() - saveStartTime;
      const remainingDisplayTime = Math.max(0, minSavingDisplayMs - elapsed);

      // If save completed too fast, wait for remaining time before transitioning
      if (remainingDisplayTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingDisplayTime));
      }

      // Update last saved data
      lastSavedDataRef.current = dataStr;
      setLastSavedAt(new Date());

      if (skipSavedStatus) {
        // Go directly to idle without showing "Saved"
        setSaveStatus('idle');
      } else {
        // Show "Saved" status
        setSaveStatus('saved');

        // Clear any existing saved display timer
        if (savedDisplayTimerRef.current) {
          clearTimeout(savedDisplayTimerRef.current);
        }

        // Reset to idle after showing "Saved" for configured duration
        savedDisplayTimerRef.current = setTimeout(() => {
          setSaveStatus('idle');
        }, savedDisplayMs);
      }

    } catch (err) {
      console.error('[useAutoSave] Save failed:', err);
      setError(err instanceof Error ? err : new Error('Save failed'));
      setSaveStatus('error');
    } finally {
      isSavingRef.current = false;

      // If there's pending data, save it now
      if (pendingDataRef.current !== null) {
        const pending = pendingDataRef.current;
        pendingDataRef.current = null;
        // Use setTimeout to avoid recursive call stack issues
        setTimeout(() => performSave(pending), 0);
      }
    }
  }, [onSave, savedDisplayMs, minSavingDisplayMs, skipSavedStatus]);

  // Force save immediately (for tab switches, etc.)
  const forceSave = useCallback(async () => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Only save if there are unsaved changes
    if (hasUnsavedChanges) {
      await performSave(data);
    }
  }, [data, hasUnsavedChanges, performSave]);

  // Reset saved data baseline (useful when initial data is loaded from API)
  const resetSavedData = useCallback((newData: any) => {
    lastSavedDataRef.current = JSON.stringify(newData);
    // Don't reset status if currently saving - let the save complete naturally
    if (!isSavingRef.current) {
      setSaveStatus('idle');
      setError(null);
    }
  }, []);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
    if (saveStatus === 'error') {
      setSaveStatus('idle');
    }
  }, [saveStatus]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!enabled || !hasUnsavedChanges) {
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If we had an error, retry immediately on next change
    if (saveStatus === 'error') {
      performSave(data);
      return;
    }

    // Set up debounced save
    debounceTimerRef.current = setTimeout(() => {
      performSave(data);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [data, enabled, hasUnsavedChanges, debounceMs, performSave, saveStatus]);

  // Cleanup on unmount - save any pending changes
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (savedDisplayTimerRef.current) {
        clearTimeout(savedDisplayTimerRef.current);
      }

      // Save any unsaved changes on unmount (fire-and-forget)
      if (enabledRef.current && !isSavingRef.current) {
        const currentDataStr = JSON.stringify(dataRef.current);
        if (currentDataStr !== lastSavedDataRef.current) {
          console.log('[useAutoSave] Saving on unmount due to unsaved changes');
          // Fire-and-forget save - we can't await in cleanup
          onSaveRef.current(dataRef.current).catch((err) => {
            console.error('[useAutoSave] Unmount save failed:', err);
          });
        }
      }
    };
  }, []);

  return {
    isSaving: saveStatus === 'saving',
    saveStatus,
    lastSavedAt,
    error,
    hasUnsavedChanges,
    forceSave,
    clearError,
    resetSavedData
  };
}

export default useAutoSave;
